import React, { useMemo } from "react";
import cx from "classnames";
import { string, bool, node, oneOfType, func } from "prop-types";
import styles from "./AccordionItem.module.scss";
import { useAccordionItem } from "./useAccordionItem";
import createContext from "../../../utils/createContext";
import Icon from "../../../assets/icons/Icon";
import { runIfFunction } from "../../../utils/function";

const [AccordionItemProvider, useAccordionItemContext] = createContext({
  name: "AccordionItem",
});

/**
 * Returns a container element, which wraps both an `AccordionHeader` and
 * `AccordionContent` component and passes context to both.
 */

function AccordionItem(props) {
  const { children, className, ...restProps } = props;
  const classNames = cx(styles.item, className);
  const { htmlProps, getAccordionItemProps, ...ctx } =
    useAccordionItem(restProps);
  const context = useMemo(() => ctx, [ctx]);

  return (
    <AccordionItemProvider value={context}>
      <div {...getAccordionItemProps()} {...htmlProps} className={classNames}>
        {runIfFunction(children, {
          isDisabled: !!context.isDisabled,
          isOpen: !!context.isOpen,
        })}
      </div>
    </AccordionItemProvider>
  );
}

export default AccordionItem;

AccordionItem.propTypes = {
  /** The content of the accordion item */
  children: oneOfType([func, node]).isRequired,
  /** For CSS customisation */
  className: string,
  /** If true, the accordion item will be disabled */
  isDisabled: bool,
  /** If true, the accordion item will be expanded */
  isOpen: bool,
};

/**
 * Returns a header for the accordion, which is a button element. A custom icon
 * can be set using the `icon` prop.
 */

export function AccordionHeader(props) {
  const { children, className, ...rest } = props;
  const { getAccordionHeaderProps, backgroundStyle, isOpen } =
    useAccordionItemContext();

  const classNames = cx(
    styles.header,
    {
      [styles.solid]: backgroundStyle === "solid",
      [styles.ghost]: backgroundStyle === "ghost",
    },
    className
  );
  const iconClassNames = cx(
    styles.icon,
    {
      [styles.open]: isOpen && !props.disableIconAnimation,
      [styles.closed]: !isOpen && !props.disableIconAnimation,
    },
    props.iconClassName
  );
  return (
    <button
      type="button"
      {...getAccordionHeaderProps(rest)}
      className={classNames}
    >
      <h4 className={styles.title}>{children}</h4>
      <Icon
        icon={props.icon ?? "GtChevronSmallDown"}
        className={iconClassNames}
      />
    </button>
  );
}

AccordionHeader.propTypes = {
  /** For CSS customisation */
  className: string,
  /** If true, rotation animation will be removed from the icon */
  disableIconAnimation: bool,
  /** The name of the custom icon to use. Default is `GtChevronDown` */
  icon: string,
  /** For CSS customisation of the chevron */
  iconClassName: string,
};

/**
 * Returns a box for content for the accordion item.
 */

export function AccordionContent(props) {
  const { className, children, ...rest } = props;
  const { isOpen, backgroundStyle } = useAccordionItemContext();
  const classNames = cx(
    styles.content,
    {
      [styles.solid]: backgroundStyle === "solid",
      [styles.ghost]: backgroundStyle === "ghost",
    },
    className
  );

  const wrapperClasses = cx(styles.contentWrapper, {
    [styles.open]: isOpen,
    [styles.closed]: !isOpen,
  });

  return (
    <div className={wrapperClasses}>
      <div {...rest} className={classNames}>
        {children}
      </div>
    </div>
  );
}

AccordionContent.propTypes = {
  /** The content to be displayed */
  children: node.isRequired,
  /** For CSS customisation */
  className: string,
};
